import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Button,
  FormControl,
  TextField,
  Typography,
  Chip,
  Box,
  InputAdornment,
  IconButton,
  Paper,
  Stack,
  Autocomplete,
} from '@mui/material';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import StopIcon from '@mui/icons-material/Stop';
import ClearIcon from '@mui/icons-material/Clear';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import api from '../../api/api';
import { styled } from '@mui/material/styles';
import { AudioRecorder } from '../../components/audioRecorder';
import { dateToString } from '../../shared/formatters';

// Custom hooks
import { useTransactionForm } from '../../hooks/useTransactionForm';
import { useTransactionData } from '../../hooks/useTransactionData';
import { useFileUpload } from '../../hooks/useFileUpload';

const FormContainer = styled(Paper)(({ theme }) => ({
  maxWidth: '800px',
  margin: 0,
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  padding: theme.spacing(3),
  borderRadius: '20px',
  backgroundColor: '#ffffff',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
    borderRadius: '16px',
    margin: 0,
  },
}));

const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fafafa',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgb(0,98,105)',
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgb(0,98,105)',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    '&.Mui-focused': {
      color: 'rgb(0,98,105)',
      fontWeight: 600,
    },
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: 'none',
  minHeight: '48px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const PrimaryButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: 'rgb(0,98,105)',
  color: 'white',
  '&:hover': {
    backgroundColor: 'rgb(0,85,91)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0, 98, 105, 0.4)',
  },
}));

const SecondaryButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.grey[200],
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
  },
}));

const DeleteButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: 'white',
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2rem',
  color: '#1a1a1a',
  marginBottom: theme.spacing(4),
  textAlign: 'left',
  position: 'relative',
  paddingBottom: theme.spacing(2),
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '60px',
    height: '4px',
    backgroundColor: 'rgb(0,98,105)',
    borderRadius: '2px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.75rem',
    marginBottom: theme.spacing(3),
  },
}));

const AudioButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isRecording',
})(({ theme, isRecording }) => ({
  backgroundColor: isRecording ? '#ef5350' : 'rgb(0,98,105)',
  color: 'white',
  borderRadius: '10px',
  padding: theme.spacing(1),
  marginLeft: theme.spacing(0.5),
  '&:hover': {
    backgroundColor: isRecording ? '#d32f2f' : 'rgb(0,85,91)',
    transform: 'scale(1.08)',
  },
  transition: 'all 0.2s ease-in-out',
  boxShadow: isRecording 
    ? '0 4px 12px rgba(239, 83, 80, 0.4)'
    : '0 4px 12px rgba(0, 98, 105, 0.4)',
  '& svg': {
    fill: 'white',
    color: 'white',
  },
}));

const AttachmentChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(0, 98, 105, 0.1)',
  color: 'rgb(0,98,105)',
  borderRadius: '10px',
  fontWeight: 600,
  padding: theme.spacing(2),
  height: 'auto',
  fontSize: '0.938rem',
  '& .MuiChip-icon': {
    color: 'rgb(0,98,105)',
  },
  '& .MuiChip-deleteIcon': {
    color: 'rgb(0,98,105)',
    '&:hover': {
      color: 'rgb(0,85,91)',
    },
  },
}));

const RecordingIndicator = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(239, 83, 80, 0.1) 0%, rgba(211, 47, 47, 0.1) 100%)',
  borderRadius: '12px',
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  border: '2px solid rgba(239, 83, 80, 0.3)',
  animation: 'pulse 2s ease-in-out infinite',
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.7,
    },
  },
}));

const ErrorBox = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(239, 83, 80, 0.1)',
  borderRadius: '12px',
  padding: theme.spacing(2),
  border: '1px solid rgba(239, 83, 80, 0.3)',
}));

const FormSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

const InputActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  '& .MuiIconButton-root': {
    color: 'rgb(0,98,105)',
    transition: 'all 0.2s ease',
    '&:hover': {
      color: 'rgb(0,85,91)',
      transform: 'scale(1.1)',
    },
  },
}));

const emptyData = {
  id: 0,
  description: '',
  value: 0,
  categoryId: '',
  financeAccountId: '',
  date: dateToString(new Date()),
};

const Edit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  // Load data and get lookup functions
  const { accounts, categories, findCategory, findAccount, findTransaction } = 
    useTransactionData();

  // Get initial data for editing
  const initialData = findTransaction(params.id) || emptyData;

  // Form management
  const {
    item,
    errors,
    updateItem,
    resetForm,
    submitForm,
    deleteAction,
    updateFromGemini,
  } = useTransactionForm(initialData);

  // Handle file upload processed by Gemini
  const handleFileProcessed = useCallback(
    (generatedData, cats, accs) => {
      updateFromGemini(generatedData, cats, accs);
    },
    [updateFromGemini]
  );

  // File upload management
  const {
    fileInputRef,
    handleUploadClick,
    handleFileChange,
    clearFileInput,
    handleImageCapture,
    deleteAttachment,
  } = useFileUpload(handleFileProcessed);

  // Audio recording
  const handleAudioRecordingComplete = useCallback(
    async (audioBlob) => {
      dispatch({ type: 'appMain/loadingStart' });
      try {
        const generatedData = await api.loadTransactionByFile(audioBlob);
        if (generatedData) {
          updateFromGemini(generatedData, categories, accounts);
        }
      } catch (error) {
        console.error('Error processing audio:', error);
      } finally {
        dispatch({ type: 'appMain/loadingEnd' });
      }
    },
    [dispatch, updateFromGemini, categories, accounts]
  );

  const audioRecorder = AudioRecorder(handleAudioRecordingComplete);

  const handleFileRemove = () => {
    clearFileInput();
    updateItem({ attachment: null });
    if (item.id) {
      deleteAttachment(item.id);
    }
  };

  const handleClear = () => {
    const clearedData = params.id ? { ...initialData } : { ...emptyData };
    resetForm(clearedData);
    audioRecorder.clearAudioBlob();
    clearFileInput();
  };

  const hiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*, .pdf, .doc, .docx, .xls, .xlsx"
      onChange={(event) => {
        const file = handleFileChange(event);
        if (file) {
          updateItem({ attachment: file });
        }
      }}
      style={{ display: 'none' }}
    />
  );

  const optionalButton = item.id ? (
    <DeleteButton
      startIcon={<DeleteIcon />}
      onClick={() => deleteAction(item.id)}
    >
      Excluir
    </DeleteButton>
  ) : (
    <SecondaryButton
      startIcon={<AddIcon />}
      onClick={(event) => submitForm(event, true)}
    >
      Salvar e novo
    </SecondaryButton>
  );

  return (
    <Box
      sx={{
        padding: { xs: 0.5, sm: 1 },
        minHeight: '100vh',
      }}
    >
      <FormContainer elevation={0}>
        <PageTitle component="h1" variant="h4">
          {item.id ? 'Editar Transação' : 'Nova Transação'}
        </PageTitle>

        <StyledForm onSubmit={(event) => submitForm(event, false)}>
          <input type="hidden" name="id" value={item ? item.id : 0} />

          <FormSection>
            <StyledTextField
              fullWidth
              label="Descrição"
              name="description"
              autoFocus
              placeholder="Ex: Compra no supermercado, Pagamento de conta..."
              value={item.description}
              onChange={(event) =>
                updateItem({ description: event.target.value })
              }
              error={!!errors.description}
              helperText={errors.description}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InputActionsContainer>
                      <IconButton
                        onClick={() => handleImageCapture(categories, accounts)}
                        size="small"
                        title="Capturar imagem"
                      >
                        <CameraAltIcon />
                      </IconButton>
                      <IconButton
                        onClick={handleUploadClick}
                        size="small"
                        title="Anexar arquivo"
                      >
                        <AttachFileIcon />
                      </IconButton>
                      <AudioButton
                        isRecording={audioRecorder.isRecording}
                        onClick={audioRecorder.isRecording ? audioRecorder.stopRecording : audioRecorder.startRecording}
                        size="small"
                        title={audioRecorder.isRecording ? "Parar gravação" : "Gravar áudio"}
                      >
                        {audioRecorder.isRecording ? <StopIcon /> : <RadioButtonCheckedIcon />}
                      </AudioButton>
                    </InputActionsContainer>
                  </InputAdornment>
                ),
              }}
            />
            {hiddenFileInput}

            {audioRecorder.isRecording && (
              <RecordingIndicator>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: '#ef5350', display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <span style={{ fontSize: '1.2rem' }}>🔴</span> Gravando áudio...
                </Typography>
              </RecordingIndicator>
            )}

            {audioRecorder.error && (
              <ErrorBox>
                <Typography variant="body2" sx={{ color: '#ef5350', fontWeight: 500 }}>
                  ❌ {typeof audioRecorder.error === 'string'
                    ? audioRecorder.error
                    : audioRecorder.error.message || 'Ocorreu um erro'}
                </Typography>
              </ErrorBox>
            )}

            {item.attachment && (
              <Box>
                <AttachmentChip
                  icon={<AttachFileIcon />}
                  label={item.attachment.name || item.attachment || 'Anexo'}
                  onDelete={handleFileRemove}
                  deleteIcon={<CloseIcon />}
                />
              </Box>
            )}
          </FormSection>

          <SectionTitle>Detalhes da Transação</SectionTitle>

          <FormSection>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <StyledTextField
                fullWidth
                label="Valor"
                name="value"
                type="number"
                placeholder="0,00"
                inputProps={{ step: '0.01' }}
                value={item.value}
                onChange={(event) =>
                  updateItem({ value: event.target.value })
                }
                error={!!errors.value}
                helperText={errors.value}
              />

              <StyledTextField
                fullWidth
                label="Data"
                name="date"
                type="date"
                value={item.date}
                onChange={(event) =>
                  updateItem({ date: event.target.value })
                }
                error={!!errors.date}
                helperText={errors.date}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <StyledFormControl error={!!errors.categoryId}>
              <Autocomplete
                value={findCategory(item.categoryId)}
                onChange={(event, newValue) => {
                  updateItem({
                    categoryId: newValue ? newValue.id : '',
                  });
                }}
                options={categories}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <StyledTextField {...params} label="Categoria" placeholder="Selecione uma categoria" error={!!errors.categoryId} helperText={errors.categoryId} />
                )}
              />
            </StyledFormControl>

            <StyledFormControl error={!!errors.financeAccountId}>
              <Autocomplete
                value={findAccount(item.financeAccountId)}
                onChange={(event, newValue) => {
                  updateItem({
                    financeAccountId: newValue ? newValue.id : '',
                  });
                }}
                options={accounts}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <StyledTextField {...params} label="Conta" placeholder="Selecione uma conta" error={!!errors.financeAccountId} helperText={errors.financeAccountId} />
                )}
              />
            </StyledFormControl>
          </FormSection>

          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            sx={{ pt: 3, mt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}
          >
            <PrimaryButton
              type="submit"
              startIcon={<SaveIcon />}
              sx={{ flex: 1 }}
            >
              Salvar
            </PrimaryButton>
            {optionalButton}
            <SecondaryButton
              type="button"
              startIcon={<ClearIcon />}
              onClick={handleClear}
            >
              {item.id === 0 ? 'Limpar' : 'Restaurar'}
            </SecondaryButton>
            <SecondaryButton
              type="button"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/transactions')}
            >
              Voltar
            </SecondaryButton>
          </Stack>
        </StyledForm>
      </FormContainer>
    </Box>
  );
};

export default Edit;
